/**
 * Demo-area users: players in the test operator's wallet (`test-operator.users`), used to launch
 * games for demos and QA. Super admins only (CrossOperatorGuard) plus the DEMOAREA bits.
 * Balances are cents in the table; the API reads and writes major units.
 * Traps: the create response echoes the request, password included (only `id` is kept here);
 * bulk remove has no dry-run, so the preview queries the same criterion first.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { decimalString, flag } from '@/api/zod-helpers';
import { listSearchSchema, toPagedQuery } from '@/lib/url-state';

export const DEMO_STATUS = { 0: 'Inactive', 1: 'Active', 2: 'Suspended' } as const;
export type DemoStatus = keyof typeof DEMO_STATUS;
export const demoStatusLabel = (s: number) => DEMO_STATUS[s as DemoStatus] ?? `Status ${s}`;

/** The DTO's documented languages. */
export const DEMO_LANGUAGES = ['en', 'es', 'fr', 'de', 'it', 'pt'] as const;

const nullableText = z
  .string()
  .nullish()
  .transform((v) => v ?? '');

export const demoUserSchema = z.object({
  id: z.coerce.number().int(),
  email: z.string(),
  /** Major units (the API returns `balance/100`), exact. */
  balance: decimalString,
  currency: z.string(),
  full_name: nullableText,
  nickname: nullableText,
  brand: nullableText,
  /** JSON column: arrives parsed or as a JSON string. */
  additional_data: z.unknown().optional(),
  is_lab: flag,
  language: nullableText,
  /** The launch session token: shown masked, revealed on demand. */
  session: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
  status: z.coerce.number().int(),
  createdAt: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
  updatedAt: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
});

export type DemoUser = z.infer<typeof demoUserSchema>;

/** Allow-listed (module-fields `demo-area`) and present in the table. `UUID`/`Name` are listed but don't exist. */
export const DEMO_SORT_FIELDS = [
  'Id',
  'Email',
  'full_name',
  'Nickname',
  'Balance',
  'Currency',
  'Brand',
  'CreatedAt',
  'UpdatedAt',
] as const;
export const DEFAULT_DEMO_SORT: SortDescriptor = { field: 'Id', dir: 'desc' };

// `Status` isn't filterable server-side, so the list offers the quick search (email) only.
export const demoSearchSchema = listSearchSchema({ sortFields: DEMO_SORT_FIELDS, filters: {} });
export type DemoSearch = z.infer<typeof demoSearchSchema>;

export function demoPagedQuery(search: DemoSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'Email', operator: 'contains', value: search.q });
  return toPagedQuery(search, { defaultSort: DEFAULT_DEMO_SORT, filters });
}

export const demoKeys = {
  all: ['demo-area'] as const,
  list: (query: PagedQuery) => ['demo-area', 'list', query] as const,
  detail: (id: number) => ['demo-area', 'detail', id] as const,
  inactive: (cutoff: string) => ['demo-area', 'inactive', cutoff] as const,
};

const listSchema = pagedSchema(demoUserSchema);

export const demoListQuery = (search: DemoSearch) => {
  const query = demoPagedQuery(search);
  return queryOptions({
    queryKey: demoKeys.list(query),
    queryFn: ({ signal }) => http('/demoArea/users', { query: encodePagedQuery(query), signal }, listSchema),
    placeholderData: keepPreviousData,
  });
};

export const demoUserQuery = (id: number) =>
  queryOptions({
    queryKey: demoKeys.detail(id),
    queryFn: ({ signal }) => http(`/demoArea/users/${id}`, { signal }, demoUserSchema),
  });

// ---- bulk remove preview --------------------------------------------------------------------

/** The server removes users whose `updatedAt` is older than 20 × 30 days (demo-area.service.ts). */
export const INACTIVE_DAYS = 600;
export const PREVIEW_LIMIT = 100;

export const inactiveCutoff = (now = Date.now()) => new Date(now - INACTIVE_DAYS * 864e5).toISOString();

/**
 * The server has no dry-run, so the preview asks the list endpoint for the same criterion. The
 * server computes its own cutoff when it runs, seconds later: a user crossing the line in between
 * would be included.
 */
export const inactivePreviewQuery = (cutoff: string) =>
  queryOptions({
    queryKey: demoKeys.inactive(cutoff),
    queryFn: ({ signal }) =>
      http(
        '/demoArea/users',
        {
          query: encodePagedQuery({
            skip: 0,
            take: PREVIEW_LIMIT,
            sort: { field: 'UpdatedAt', dir: 'asc' },
            filters: [{ field: 'UpdatedAt', operator: 'lt', value: cutoff }],
          }),
          signal,
        },
        listSchema,
      ),
  });

// ---- writes --------------------------------------------------------------------------------

/** CreateDemoAreaDto / UpdateDemoAreaDto fields: the global pipe rejects anything else. */
export interface DemoUserWrite {
  email?: string;
  password?: string;
  /** Major units; the server converts to cents. */
  balance?: number;
  currency?: string;
  full_name?: string;
  nickname?: string;
  brand?: string;
  language?: string;
  is_lab?: boolean;
  status?: number;
  session?: string;
  /** JSON string; null clears it. */
  additional_data?: string | null;
}

const createdSchema = z.object({ id: z.coerce.number().int() });

export function useCreateDemoUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DemoUserWrite) => http('/demoArea/users', { method: 'POST', body }, createdSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: demoKeys.all });
    },
  });
}

export function useUpdateDemoUser(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DemoUserWrite) => http(`/demoArea/users/${id}`, { method: 'PATCH', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: demoKeys.all });
    },
  });
}

export function useDeleteDemoUser(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`/demoArea/users/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: demoKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: demoKeys.all });
    },
  });
}

const bulkResultSchema = z.object({ message: z.string().optional() }).passthrough();

export function useBulkRemoveInactive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http('/demoArea/users/bulk-remove', { method: 'DELETE' }, bulkResultSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: demoKeys.all });
    },
  });
}

/** Major-unit decimal string → integer cents string, exactly (for `formatMoney`). */
export function majorToCents(major: string): string {
  const [whole = '0', frac = ''] = major.replace(/^-/, '').split('.');
  const cents = `${whole}${frac.padEnd(2, '0').slice(0, 2)}${frac.length > 2 ? `.${frac.slice(2)}` : ''}`;
  return `${major.startsWith('-') ? '-' : ''}${cents.replace(/^0+(?=\d)/, '')}`;
}
